import { useMemo, type ButtonHTMLAttributes, type ReactNode } from 'react';
import type { Icon } from '@tabler/icons-react';
import { useFela } from 'react-fela';
import { matchPath, useLocation } from 'react-router-dom-v5-compat';

import { Link, type ExternalLinkProps, type InternalLinkProps } from '@/modules/ui';

import * as felaRules from './MenuItem.rules';

export interface MenuItemProps {
    icon?: Icon;
    trailing?: ReactNode;
    link?: ExternalLinkProps | InternalLinkProps;
    button?: ButtonHTMLAttributes<HTMLButtonElement>;
    children: ReactNode;
    additionalRoutesWithActiveClassName?: string[];
}

export const MenuItem = ({ icon: ItemIcon, trailing, link, button, children, additionalRoutesWithActiveClassName }: MenuItemProps) => {
    const { css } = useFela();
    const { pathname } = useLocation();

    const isCurrentRouteActive = useMemo(() => {
        if (!additionalRoutesWithActiveClassName) return false;

        return additionalRoutesWithActiveClassName.some(route => matchPath(pathname, route));
    }, [pathname, additionalRoutesWithActiveClassName]);

    const content = (
        <>
            {ItemIcon ? <ItemIcon size={24} className={css(felaRules.icon)} /> : null}
            <span className={css(felaRules.label)}>{children}</span>
            {trailing}
        </>
    );

    if (link) {
        if (link.type === 'router') {
            return (
                <Link {...link} customStyle={felaRules.item} overrideActiveClassName={isCurrentRouteActive}>
                    {content}
                </Link>
            );
        }
        return (
            <Link {...link} customStyle={felaRules.item}>
                {content}
            </Link>
        );
    }

    return (
        <button className={css(felaRules.button)} {...button}>
            {content}
        </button>
    );
};
